import React, { useState } from 'react';
import axios from 'axios';
import { Eye, EyeOff, ArrowRight, CheckCircle, AlertCircle, Lock } from 'lucide-react';
import { API_URL } from '../styles/shared';
import MarketPulzeLogo from '../components/MarketPulzeLogo';

const getPasswordStrength = (pw) => {
  if (!pw) return { score: 0, label: '', color: 'transparent' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { score, label: 'Weak', color: '#ff4444' };
  if (score <= 2) return { score, label: 'Fair', color: '#ffaa00' };
  if (score <= 3) return { score, label: 'Good', color: '#00d4ff' };
  return { score, label: 'Strong', color: '#00ff88' };
};

const ResetPasswordPage = ({ token }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [countdown, setCountdown] = useState(3);

  const pwStrength = getPasswordStrength(newPassword);

  const validate = () => {
    const e = {};
    if (!newPassword) e.newPassword = 'New password is required';
    else if (newPassword.length < 8) e.newPassword = 'Password must be at least 8 characters';
    if (!confirmPassword) e.confirmPassword = 'Please confirm your password';
    else if (newPassword !== confirmPassword) e.confirmPassword = 'Passwords do not match';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/reset-password`, { token, newPassword });
      if (res.data.success) {
        setSuccess(true);
        const timer = setInterval(() => {
          setCountdown(c => {
            if (c <= 1) { clearInterval(timer); window.location.href = '/'; }
            return c - 1;
          });
        }, 1000);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Password reset failed. The link may have expired.';
      setGlobalError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#050d1a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', fontFamily: '"DM Sans", system-ui, sans-serif' }}>
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '3rem' }}>
        <MarketPulzeLogo size={36} />
        <span style={{ fontSize: '1.25rem', fontWeight: 900, background: 'linear-gradient(135deg,#00ff88,#00d4ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>MarketPulze</span>
      </div>

      <div style={{ maxWidth: '440px', width: '100%', background: 'linear-gradient(135deg,rgba(12,25,50,0.9),rgba(18,32,60,0.9))', backdropFilter: 'blur(20px)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: '24px', padding: '3rem 2.5rem' }}>

        {success ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(0,255,136,0.1)', border: '2px solid rgba(0,255,136,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <CheckCircle size={36} style={{ color: '#00ff88' }} />
            </div>
            <h2 style={{ color: '#f8fafc', fontSize: '1.75rem', fontWeight: 900, marginBottom: '0.75rem' }}>Password updated!</h2>
            <p style={{ color: '#94a3b8', lineHeight: 1.7, marginBottom: '2rem' }}>
              Your password has been successfully reset. You can now sign in with your new password.
            </p>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(0,255,136,0.08)', border: '2px solid rgba(0,255,136,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
              <span style={{ fontSize: '1.4rem', fontWeight: 900, color: '#00ff88' }}>{countdown}</span>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '1.5rem' }}>Redirecting to login in {countdown}s...</p>
            <a href="/" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.9rem', background: 'linear-gradient(135deg,#00ff88,#00d4ff)', borderRadius: '10px', color: '#050d1a', fontWeight: 800, fontSize: '0.95rem', textDecoration: 'none' }}>
              Sign In Now <ArrowRight size={16} />
            </a>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', marginBottom: '1.75rem' }}>
              <div style={{ width: 44, height: 44, borderRadius: '12px', background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Lock size={20} style={{ color: '#00d4ff' }} />
              </div>
              <div>
                <h2 style={{ color: '#f8fafc', fontSize: '1.4rem', fontWeight: 900, margin: 0 }}>Reset your password</h2>
                <p style={{ color: '#64748b', fontSize: '0.8rem', margin: 0 }}>Choose a strong, unique password</p>
              </div>
            </div>

            {globalError && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', background: 'rgba(255,68,68,0.08)', border: '1px solid rgba(255,68,68,0.25)', borderRadius: '10px', padding: '0.9rem 1rem', marginBottom: '1.5rem' }}>
                <AlertCircle size={16} style={{ color: '#ff4444', flexShrink: 0, marginTop: '1px' }} />
                <div>
                  <span style={{ fontSize: '0.85rem', color: '#ff6b6b' }}>{globalError}</span>
                  <div style={{ marginTop: '0.4rem' }}>
                    <a href="/" style={{ fontSize: '0.8rem', color: '#00d4ff', textDecoration: 'none' }}>← Back to login</a>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* New password */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.45rem', letterSpacing: '0.04em' }}>
                  <span>NEW PASSWORD</span>
                  {newPassword && <span style={{ color: pwStrength.color, fontWeight: 700 }}>{pwStrength.label}</span>}
                </label>
                <div style={{ position: 'relative' }}>
                  <input type={showNew ? 'text' : 'password'} value={newPassword}
                    onChange={e => { setNewPassword(e.target.value); setErrors(p => ({ ...p, newPassword: '' })); }}
                    placeholder="Minimum 8 characters"
                    style={{ width: '100%', padding: '0.85rem 3rem 0.85rem 1rem', background: 'rgba(10,20,45,0.6)', border: `1.5px solid ${errors.newPassword ? '#ff4444' : 'rgba(255,255,255,0.08)'}`, borderRadius: '10px', color: '#f8fafc', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  <button type="button" onClick={() => setShowNew(s => !s)} style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 0 }}>
                    {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {newPassword && (
                  <div style={{ height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden', marginTop: '0.4rem' }}>
                    <div style={{ height: '100%', width: `${(pwStrength.score / 4) * 100}%`, background: pwStrength.color, borderRadius: '2px', transition: 'all 0.3s' }} />
                  </div>
                )}
                {errors.newPassword && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.4rem' }}>
                    <AlertCircle size={12} style={{ color: '#ff4444' }} />
                    <span style={{ fontSize: '0.75rem', color: '#ff4444' }}>{errors.newPassword}</span>
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#94a3b8', marginBottom: '0.45rem', letterSpacing: '0.04em' }}>CONFIRM PASSWORD</label>
                <div style={{ position: 'relative' }}>
                  <input type={showConfirm ? 'text' : 'password'} value={confirmPassword}
                    onChange={e => { setConfirmPassword(e.target.value); setErrors(p => ({ ...p, confirmPassword: '' })); }}
                    placeholder="Repeat your new password"
                    style={{ width: '100%', padding: '0.85rem 3rem 0.85rem 1rem', background: 'rgba(10,20,45,0.6)', border: `1.5px solid ${errors.confirmPassword ? '#ff4444' : confirmPassword && confirmPassword === newPassword ? 'rgba(0,255,136,0.4)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '10px', color: '#f8fafc', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  <button type="button" onClick={() => setShowConfirm(s => !s)} style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 0 }}>
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  {confirmPassword && confirmPassword === newPassword && (
                    <CheckCircle size={16} style={{ color: '#00ff88', position: 'absolute', right: '2.5rem', top: '50%', transform: 'translateY(-50%)' }} />
                  )}
                </div>
                {errors.confirmPassword && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.4rem' }}>
                    <AlertCircle size={12} style={{ color: '#ff4444' }} />
                    <span style={{ fontSize: '0.75rem', color: '#ff4444' }}>{errors.confirmPassword}</span>
                  </div>
                )}
              </div>

              {/* Password requirements */}
              <div style={{ background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: '8px', padding: '0.9rem 1rem', marginBottom: '1.5rem' }}>
                <p style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, marginBottom: '0.5rem', letterSpacing: '0.04em' }}>PASSWORD REQUIREMENTS</p>
                {[
                  { rule: 'At least 8 characters', met: newPassword.length >= 8 },
                  { rule: 'One uppercase letter', met: /[A-Z]/.test(newPassword) },
                  { rule: 'One number', met: /[0-9]/.test(newPassword) },
                ].map(({ rule, met }) => (
                  <div key={rule} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: met ? '#00ff88' : 'rgba(255,255,255,0.15)', transition: 'background 0.2s' }} />
                    <span style={{ fontSize: '0.78rem', color: met ? '#94a3b8' : '#475569', transition: 'color 0.2s' }}>{rule}</span>
                  </div>
                ))}
              </div>

              <button type="submit" disabled={loading} style={{ width: '100%', padding: '0.95rem', background: loading ? 'rgba(0,255,136,0.3)' : 'linear-gradient(135deg,#00ff88,#00d4ff)', border: 'none', borderRadius: '10px', color: '#050d1a', fontWeight: 800, fontSize: '0.95rem', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                {loading ? '⏳ Updating password...' : <> Update Password <ArrowRight size={16} /> </>}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
              <a href="/" style={{ fontSize: '0.85rem', color: '#475569', textDecoration: 'none' }}>← Back to login</a>
            </div>
          </>
        )}
      </div>
      <style>{`input::placeholder { color: #334155; }`}</style>
    </div>
  );
};

export default ResetPasswordPage;
